/**
 * @file The file reader service
 * @author Heino Lenting
 */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SettingsService } from '../settings.service';
/**
 * Class for the file read service base
 */
var FileReaderService = /** @class */ (function () {
    /**
     * Constructs a FileReaderService.
     * @param alertsService Service for giving user feedback
     */
    function FileReaderService(http, settingsService) {
        this.http = http;
        /**
         * Gets, sets the standard file upload url.
         * @private
         * @type {string}
         * @memberOf FileReaderService
         */
        this.uploadBaseUrl = null;
        /** The allowed mime types */
        this.allowedMimeTypes = [];
        /** The allowed file size in bytes */
        this.allowedFileSizeInBytes = 1000000; // = Megabyte
        var settings = settingsService.settings.frontend.domains.find(function (it) { return it.name === 'settings'; });
        if (settings == null) {
            throw new Error('Domain settings is missing');
        }
        this.uploadBaseUrl = settings.uri;
    }
    /**
     * Register the file reader component
     * @param component The file reader component
     */
    FileReaderService.prototype.registerComponent = function (component) {
        this.component = component;
    };
    /**
     * Click on the input element to open the file window
     * @param file The file to read
     */
    FileReaderService.prototype.pickFile = function (allowedMimeTypes, allowedExtensions) {
        var _this = this;
        // Make sure the component for input element interaction exists
        if (!this.component) {
            return Promise.reject(new Error('File reader component not registerd'));
        }
        // Save the allowed mime type
        this.allowedMimeTypes = allowedMimeTypes;
        // Return the promise, resolving and rejecting will happen when the input is changed
        return new Promise(function (resolve, reject) {
            _this.resolve = resolve;
            _this.reject = reject;
            // Let's start the 'input click dance'...
            _this.component.openFileWindow(allowedExtensions);
        });
    };
    /**
     * Reads a file, returns a FileData promise
     * @param file The file to read
     */
    FileReaderService.prototype.readFile = function (file) {
        var _this = this;
        // Make sure we have a file
        if (!file) {
            return this.reject(new Error('No file selected'));
        }
        // Check mime type
        if (!this.allowedMimeTypes.includes(file.type)) {
            return this.reject(new Error('Invalid file type'));
        }
        // Check file size
        if (file.size > this.allowedFileSizeInBytes) {
            return this.reject(new Error('File too large'));
        }
        // Create file reader and set onloaded
        var reader = new FileReader();
        reader.onloadend = function (e) {
            _this.resolve({ fileName: file.name, data: reader.result });
        };
        // Start reading
        reader.readAsArrayBuffer(file);
    };
    /**
     * Returns an empty promise, to finish the action (and
     * be sure the action button is re-enabled again).
     */
    FileReaderService.prototype.cancelFile = function () {
        // Resolve with an empty message. This will not be shown.
        return this.resolve();
    };
    /**
     * Standard file upload method.
     * @param {string} uploadType Backend processing type of the uploaded data.
     * @param {FileData} fileData The file data.
     * @memberOf FileReaderService
     */
    FileReaderService.prototype.uploadFile = function (uploadType, fileData, toMessageQueue) {
        var file = new File([new Blob([new Uint8Array(fileData.data, 0, fileData.data.byteLength)])], fileData.fileName);
        var data = new FormData();
        data.append('file', file, fileData.fileName);
        var headers = new HttpHeaders().append('Upload-Type', uploadType);
        var uploadEndPoint = toMessageQueue ? this.uploadBaseUrl + 'MessageQueueUpload' : this.uploadBaseUrl + 'DomainUpload';
        return this.http.post(uploadEndPoint, data, { headers: headers }).toPromise();
    };
    return FileReaderService;
}());
export { FileReaderService };
